import type {ReactElement} from 'react';
import {AnimationStylesPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/animation-styles-panel/animation-styles-panel';
import type {AnimationSpeed, SlideType} from '@PosterWhiteboard/classes/item-animation.class';
import {PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromItemOutroAnimation} from '@Hooks/poster-editor/useGetKeyFromItemOutroAnimation';
import {useAppSelector} from '@/hooks';
import {getCurrentPage} from '@Components/poster-editor/poster-editor-reducer';
import {PreviewType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {AnimationType} from '@PosterWhiteboard/items/item/item.types';

export function ItemOutroAnimationPanel(): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const currentPageObject = useAppSelector(getCurrentPage);
  const type = useGetKeyFromItemOutroAnimation('type');
  const speed = useGetKeyFromItemOutroAnimation('speed');
  const direction = useGetKeyFromItemOutroAnimation('direction');

  if (!activeItem) {
    throw new Error('Unable to show animation options since the activeItem is null');
  }

  if (!type || !speed || !direction || !currentPageObject) {
    return null;
  }

  const onAnimationTypeModified = (animationType: AnimationType): void => {
    trackPosterBuilderGA4Events(GA4EventName.ANIMATION_ITEM_EXIT, {[GA4EventParamName.ANIMATION_TYPE]: animationType, [GA4EventParamName.ITEM_TYPE]: activeItem.gitype});
    void activeItem.updateFromObject(
      {
        outroAnimation: {
          type: animationType,
          direction: animationType === AnimationType.ZOOM ? PositionType.LEFT : PositionType.RIGHT,
        },
        onPageEndTime: animationType === AnimationType.NONE ? currentPageObject?.duration : activeItem.onPageEndTime,
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: true}
    );
  };
  const onAnimationSpeedModified = (undoable: boolean, replayPosterOnChange: boolean, speed: AnimationSpeed): void => {
    void activeItem.updateFromObject(
      {
        outroAnimation: {
          speed: speed,
        },
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: replayPosterOnChange, undoable: undoable}
    );
  };
  const onAnimationDirectionModified = (direction: SlideType | PositionType): void => {
    void activeItem.updateFromObject(
      {
        outroAnimation: {
          direction: direction,
        },
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: true}
    );
  };

  const getAnimations = (): AnimationType[] => {
    const animationStyles = [AnimationType.NONE];
    if (activeItem.isText() || activeItem.isSlideshow()) {
      animationStyles.push(AnimationType.BLOCK);
    }
    animationStyles.push(
      AnimationType.FADE,
      AnimationType.JELLO,
      AnimationType.PAN,
      AnimationType.POP,
      AnimationType.ROTATE,
      AnimationType.SCALE,
      AnimationType.SLIDE,
      AnimationType.WIPE,
      AnimationType.PEEK,
      AnimationType.ZOOM
    );
    return animationStyles;
  };

  const getPreviewType = (): PreviewType => {
    if (activeItem?.isText() || activeItem?.isMenu() || activeItem?.isFancyText() || activeItem?.isSchedule() || activeItem?.isTableItem() || activeItem?.isTab()) {
      return PreviewType.TEXT;
    }
    if (activeItem?.isSlideshow() && activeItem.slides.getLastSlide().isTextSlide()) {
      return PreviewType.TEXT;
    }
    if (activeItem?.isSticker() || activeItem.isRectangle()|| activeItem.isLine() || activeItem.isVector() || activeItem.isQRItem()) {
      return PreviewType.ELEMENT;
    }
    return PreviewType.MEDIA;
  };

  return (
    <AnimationStylesPanel
      selectedAnimation={type}
      onAnimationTypeModified={onAnimationTypeModified}
      onAnimationSpeedModified={onAnimationSpeedModified}
      onAnimationDirectionModified={onAnimationDirectionModified}
      speed={speed}
      direction={direction}
      animations={getAnimations()}
      showInGrid={true}
      previewType={getPreviewType()}
    />
  );
}
